/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-07-26 08:00:56
 *
 *******************************************************/
// @flow

import React, { Component } from 'react';
import { Field, FieldArray } from 'redux-form';
import { FormLayout, TextStyle } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import SearchTermTable from '../common/search-term-table';
import ProductTable from '../common/product-table';
import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import {
  NUMBER_PRODUCT_SUGGESTIONS,
  NUMBER_SEARCH_TERM_SUGGESTIONS
} from 'src/constants/instant-search-display';
import { RenderChoice2 } from 'src/components/views/common/forms/render-choice-2';
import { SEARCH_TERM_LIST, PRODUCT_LIST } from 'src/constants/instant-search-display';
import { buildOptionList } from 'src/helpers';

type Props = WithI18nProps;
// DONE
class NoSearchResultSuggestions extends Component<Props> {
  state = {
    searchTermSuggestionsChoices: [],
    productSuggestionsChoices: []
  };

  componentDidMount() {
    const { i18n } = this.props;
    const searchTermSuggestionsChoices = buildOptionList(SEARCH_TERM_LIST, i18n);
    const productSuggestionsChoices = buildOptionList(PRODUCT_LIST, i18n);

    this.setState({
      searchTermSuggestionsChoices,
      productSuggestionsChoices
    });
  }

  render() {
    const {
      swapOptionValues,
      enableSearchTermSuggestions,
      enableProductSuggestions,
      searchTermSuggestionsList,
      searchTermSuggestionsType,
      productSuggestionsList,
      productSuggestionsType,
      i18n
    } = this.props;

    const { searchTermSuggestionsChoices, productSuggestionsChoices } = this.state;

    return (
      <div className="isw-without-results groupFields">
        <p>
          {i18n.translate('AdminHTML.InstantSearchDisplay.NoSearchResultSuggestions.paragraph')}
        </p>
        <div className="groupFields-item">
          <Field
            name="enableSearchTermSuggestions"
            component={RenderToggleSwitch}
            label={i18n.translate(
              'AdminHTML.InstantSearchDisplay.NoSearchResultSuggestions.noSearchResultSuggestions_Field1_Label'
            )}
          />
          {enableSearchTermSuggestions && (
            <FormLayout>
              <Field
                name="searchTermSuggestionsLabel"
                component={RenderTextField}
                label={i18n.translate(
                  'AdminHTML.InstantSearchDisplay.NoSearchResultSuggestions.noSearchResultSuggestions_Field_Label'
                )}
              />
              <div>
                <p style={{ marginBottom: '0.25rem' }}>
                  {i18n.translate('AdminHTML.InstantSearchDisplay.Common.searchTermTable_Label')}
                </p>
                <div className="radioButtonGroup">
                  <Field
                    name="searchTermSuggestionsType"
                    component={RenderChoice2}
                    choices={searchTermSuggestionsChoices}
                    onChange={() => swapOptionValues('searchTerm')}
                  />
                  {searchTermSuggestionsType === 'manually' && (
                    <div>
                      <FieldArray
                        name="searchTermSuggestionsList"
                        component={SearchTermTable}
                        searchTermSuggestionsList={searchTermSuggestionsList}
                      />
                    </div>
                  )}
                </div>
              </div>
              {searchTermSuggestionsType === 'manually' &&
                searchTermSuggestionsList.length === NUMBER_SEARCH_TERM_SUGGESTIONS && (
                  <p>
                    <TextStyle variation="negative">
                      <TextStyle variation="strong">
                        {i18n.translate(
                          'AdminHTML.InstantSearchDisplay.NoSearchResultSuggestions.noSearchResultSuggestions_Notice'
                        )}
                      </TextStyle>
                    </TextStyle>{' '}
                    {i18n.translate(
                      'AdminHTML.InstantSearchDisplay.NoSearchResultSuggestions.noSearchResultSuggestions_Paragraph2',
                      {
                        t: (
                          <TextStyle variation="strong">
                            {i18n.translate(
                              'AdminHTML.InstantSearchDisplay.NoSearchResultSuggestions.noSearchResultSuggestions_Paragraph2_t',
                              {
                                nt_number: NUMBER_SEARCH_TERM_SUGGESTIONS
                              }
                            )}
                          </TextStyle>
                        )
                      }
                    )}
                    <br />
                    {i18n.translate(
                      'AdminHTML.InstantSearchDisplay.NoSearchResultSuggestions.noSearchResultSuggestions_Paragraph3'
                    )}
                  </p>
                )}
            </FormLayout>
          )}
        </div>
        <div className="groupFields-item">
          <Field
            name="enableProductSuggestions"
            component={RenderToggleSwitch}
            label={i18n.translate(
              'AdminHTML.InstantSearchDisplay.NoSearchResultSuggestions.noSearchResultSuggestions_Field3_Label'
            )}
          />
          {enableProductSuggestions && (
            <FormLayout>
              <Field
                name="productSuggestionsLabel"
                component={RenderTextField}
                label={i18n.translate(
                  'AdminHTML.InstantSearchDisplay.NoSearchResultSuggestions.noSearchResultSuggestions_Field_Label'
                )}
              />
              <div>
                <p style={{ marginBottom: '0.25rem' }}>
                  {i18n.translate('AdminHTML.InstantSearchDisplay.Common.productTable_Label')}
                </p>
                <div className="radioButtonGroup">
                  <Field
                    name="productSuggestionsType"
                    component={RenderChoice2}
                    choices={productSuggestionsChoices}
                    onChange={() => swapOptionValues('product')}
                  />
                  {productSuggestionsType === 'manually' && (
                    <div>
                      <FieldArray
                        name="productSuggestionsList"
                        component={ProductTable}
                        productSuggestionsList={productSuggestionsList}
                        fieldName="productSuggestionsList"
                        source="noSearchResultSuggestions"
                      />
                    </div>
                  )}
                </div>
              </div>
              {productSuggestionsType === 'manually' &&
                productSuggestionsList.length === NUMBER_PRODUCT_SUGGESTIONS && (
                  <p>
                    <TextStyle variation="negative">
                      <TextStyle variation="strong">
                        {i18n.translate(
                          'AdminHTML.InstantSearchDisplay.NoSearchResultSuggestions.noSearchResultSuggestions_Notice'
                        )}
                      </TextStyle>
                    </TextStyle>{' '}
                    {i18n.translate(
                      'AdminHTML.InstantSearchDisplay.NoSearchResultSuggestions.noSearchResultSuggestions_Paragraph4',
                      {
                        t: (
                          <TextStyle variation="strong">
                            {i18n.translate(
                              'AdminHTML.InstantSearchDisplay.NoSearchResultSuggestions.noSearchResultSuggestions_Paragraph4_t',
                              {
                                nt_number: NUMBER_PRODUCT_SUGGESTIONS
                              }
                            )}
                          </TextStyle>
                        )
                      }
                    )}
                    <br />
                    {i18n.translate(
                      'AdminHTML.InstantSearchDisplay.NoSearchResultSuggestions.noSearchResultSuggestions_Paragraph5'
                    )}
                  </p>
                )}
            </FormLayout>
          )}
        </div>
      </div>
    );
  }
}
export default withI18n()(NoSearchResultSuggestions);
